import React, { useEffect, useMemo, useState } from "react";
import { agentApi } from "../services/api";
import { allowedOriginsToInput, parseAllowedOriginsInput } from "../lib/embedOrigins";

const EMBED_SIZES = [
  { id: "small", label: "Small", width: 380, height: 520 },
  { id: "medium", label: "Medium", width: 420, height: 600 },
  { id: "large", label: "Large", width: 480, height: 720 },
];

const AVATAR_OPTIONS = [
  { id: "none", label: "No avatar" },
  { id: "image", label: "Still image" },
  { id: "video", label: "Video" },
  { id: "voice", label: "Voice input only" },
];

function EmbedPreviewPlaceholder({ width, height, title, primaryColor, avatarMode }) {
  const darken = (hex, factor = 0.15) => {
    const normalized = (hex || "#459EA3").replace(/^#/, "");
    if (!/^[0-9A-Fa-f]{6}$/.test(normalized)) return hex;
    const r = Math.max(0, (parseInt(normalized.slice(0, 2), 16) * (1 - factor)) | 0);
    const g = Math.max(0, (parseInt(normalized.slice(2, 4), 16) * (1 - factor)) | 0);
    const b = Math.max(0, (parseInt(normalized.slice(4, 6), 16) * (1 - factor)) | 0);
    return `#${r.toString(16).padStart(2, "0")}${g.toString(16).padStart(2, "0")}${b.toString(16).padStart(2, "0")}`;
  };

  const headerBg = `linear-gradient(to right, ${primaryColor || "#459EA3"}, ${darken(primaryColor)})`;
  const isVoice = avatarMode === "voice";
  const hasAvatar = avatarMode === "image" || avatarMode === "video" || isVoice;

  return (
    <div
      className="flex shrink-0 flex-col overflow-hidden rounded-xl border border-gray-300 bg-white shadow-lg"
      style={{ width, height }}
    >
      <div className="flex shrink-0 items-center justify-between px-3 py-2 text-white" style={{ background: headerBg }}>
        <span className="truncate text-sm font-semibold">{title || "Chat"}</span>
        <span className="h-6 w-6 rounded bg-white/20" />
      </div>
      {hasAvatar && (
        <div className="flex shrink-0 items-center justify-center border-b border-gray-200 bg-gray-100" style={{ height: isVoice ? "70%" : "33%" }}>
          <div className="flex h-16 w-16 items-center justify-center rounded-full bg-gray-300 text-xs text-gray-500">
            {avatarMode === "image" && "IMG"}
            {avatarMode === "video" && "VID"}
            {isVoice && "MIC"}
          </div>
        </div>
      )}
      <div className="flex min-h-0 flex-1 flex-col bg-[#F9F9F9] p-2">
        <div className="mb-2 flex justify-end">
          <div className="max-w-[80%] rounded-lg px-2 py-1.5 text-xs text-white" style={{ background: primaryColor || "#459EA3" }}>
            User message
          </div>
        </div>
        <div className="flex justify-start">
          <div className="max-w-[80%] rounded-lg border border-gray-200 bg-white px-2 py-1.5 text-xs text-gray-700">
            Reply...
          </div>
        </div>
        <div className="mt-auto flex gap-1 border-t border-gray-200 pt-2">
          <div className="h-8 flex-1 rounded-lg bg-gray-200" />
          <div className="h-8 w-8 rounded-lg bg-gray-300" />
          <div className="h-8 w-8 shrink-0 rounded-lg" style={{ backgroundColor: primaryColor || "#459EA3" }} />
        </div>
      </div>
    </div>
  );
}

function EmbedDeployPanel({
  scriptId,
  avatarMode = "none",
  width = 420,
  height = 600,
  title = "Chat with Maya",
  primaryColor = "#459EA3",
  onCopy,
  copyDisabled = false,
  copyLabel = "Save and copy embed code",
  copyError = "",
}) {
  const [copied, setCopied] = useState(false);
  const [copying, setCopying] = useState(false);

  const baseUrl = typeof window !== "undefined"
    ? `${window.location.origin}${(window.location.pathname || "").replace(/\/$/, "")}#/embed`
    : "https://go.freshroad.com/#/embed";
  const params = new URLSearchParams({ scriptId });
  if (avatarMode && avatarMode !== "none") params.set("avatarMode", avatarMode);
  if (title && title.trim()) params.set("title", title.trim());
  const hex = (primaryColor || "").replace(/^#/, "");
  if (/^[0-9A-Fa-f]{6}$/.test(hex)) params.set("primaryColor", hex);
  const embedUrl = `${baseUrl}?${params.toString()}`;
  const iframeSnippet = `<iframe src="${embedUrl}" width="${width}" height="${height}" frameborder="0" title="Chat"></iframe>`;

  const copyToClipboard = (text) => navigator.clipboard.writeText(text);

  const handleCopy = async () => {
    setCopying(true);
    try {
      await onCopy(iframeSnippet);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } finally {
      setCopying(false);
    }
  };

  return (
    <div className="space-y-5">
      <div>
        <h4 className="mb-1 text-sm font-semibold text-gray-800">1. Add this code to your webpage</h4>
        <p className="mb-2 text-sm text-gray-600">
          The snippet below includes your title, colour, size, and avatar. Embed restrictions are saved on the agent before the code is copied.
        </p>
        <pre className="overflow-x-auto whitespace-pre-wrap break-all rounded-lg bg-gray-900 p-4 font-mono text-sm text-gray-100">
          {iframeSnippet}
        </pre>
        <button
          type="button"
          onClick={handleCopy}
          disabled={copyDisabled || copying}
          className="mt-2 w-full rounded-lg bg-[#459EA3] py-3 font-medium text-white transition-colors hover:bg-[#3d8a8e] disabled:cursor-not-allowed disabled:bg-gray-300"
        >
          {copied ? "Copied!" : copying ? "Saving settings..." : copyLabel}
        </button>
        {copyError ? <p className="mt-2 text-sm text-rose-600">{copyError}</p> : null}
      </div>
      <div>
        <h4 className="mb-1 text-sm font-semibold text-gray-800">2. Script ID</h4>
        <p className="mb-2 text-sm text-gray-600">Use this ID if you need to reference the agent elsewhere.</p>
        <div className="flex gap-2">
          <code className="flex-1 truncate rounded border border-gray-200 bg-gray-100 px-3 py-2 text-sm">{scriptId}</code>
          <button
            type="button"
            onClick={() => copyToClipboard(scriptId)}
            className="shrink-0 rounded-lg bg-gray-200 px-4 py-2 text-sm font-medium text-gray-800 hover:bg-gray-300"
          >
            Copy
          </button>
        </div>
      </div>
    </div>
  );
}

export default function EmbedDeployModal({ open, onClose, scripts = [], initialScriptId = null }) {
  const [selectedScriptId, setSelectedScriptId] = useState(initialScriptId || "");
  const [embedSize, setEmbedSize] = useState("medium");
  const [avatarMode, setAvatarMode] = useState("none");
  const [embedTitle, setEmbedTitle] = useState("Chat with Maya");
  const [embedPrimaryColor, setEmbedPrimaryColor] = useState("#459EA3");
  const [loadingSettings, setLoadingSettings] = useState(false);
  const [settingsError, setSettingsError] = useState("");
  const [copyError, setCopyError] = useState("");
  const [fetchedSettings, setFetchedSettings] = useState({});
  const [allowedOriginsInput, setAllowedOriginsInput] = useState("");

  const effectiveScriptId = selectedScriptId || initialScriptId;
  const selectedScript = (scripts || []).find((script) => script._id === effectiveScriptId);
  const sizePreset = EMBED_SIZES.find((size) => size.id === embedSize) || EMBED_SIZES[1];

  const parsedOrigins = useMemo(() => parseAllowedOriginsInput(allowedOriginsInput), [allowedOriginsInput]);
  const allowlistErrors = useMemo(() => {
    const errors = [...parsedOrigins.errors];
    if (parsedOrigins.allowedOrigins.length === 0) {
      errors.push("Add at least one allowed site origin.");
    }
    return errors;
  }, [parsedOrigins]);

  useEffect(() => {
    if (open && initialScriptId) setSelectedScriptId(initialScriptId);
  }, [open, initialScriptId]);

  useEffect(() => {
    if (!open || !effectiveScriptId) {
      setFetchedSettings({});
      setAllowedOriginsInput("");
      setSettingsError("");
      setCopyError("");
      return;
    }

    let active = true;
    setLoadingSettings(true);
    setSettingsError("");
    setCopyError("");

    agentApi.get(`/api/script/${effectiveScriptId}`)
      .then((response) => {
        if (!active) return;
        const doc = response?.data?.script || {};
        const nextSettings = doc?.settings && typeof doc.settings === "object" && !Array.isArray(doc.settings) ? doc.settings : {};
        const embed = nextSettings.embed && typeof nextSettings.embed === "object" && !Array.isArray(nextSettings.embed) ? nextSettings.embed : {};
        setFetchedSettings(nextSettings);
        setAllowedOriginsInput(allowedOriginsToInput(Array.isArray(embed.allowedOrigins) ? embed.allowedOrigins : []));
      })
      .catch((error) => {
        if (!active) return;
        const message = error?.response?.data?.message || error?.message || "Failed to load embed settings.";
        setFetchedSettings({});
        setAllowedOriginsInput("");
        setSettingsError(message);
      })
      .finally(() => {
        if (active) setLoadingSettings(false);
      });

    return () => {
      active = false;
    };
  }, [open, effectiveScriptId]);

  if (!open) return null;

  const handleBackdrop = () => {
    onClose();
  };

  const saveEmbedSettings = async () => {
    if (!effectiveScriptId) return;
    setCopyError("");

    const nextSettings = {
      ...fetchedSettings,
      embed: {
        allowlistEnabled: true,
        allowedOrigins: parsedOrigins.allowedOrigins,
      },
    };

    try {
      await agentApi.post("/api/update-script", {
        _id: effectiveScriptId,
        settings: nextSettings,
      });
      setFetchedSettings(nextSettings);
    } catch (error) {
      const message = error?.response?.data?.message || error?.message || "Failed to save embed settings.";
      setCopyError(message);
      throw error;
    }
  };

  const handleCopySnippet = async (iframeSnippet) => {
    await saveEmbedSettings();
    await navigator.clipboard.writeText(iframeSnippet);
  };

  const copyDisabled = !effectiveScriptId || loadingSettings || !!settingsError || allowlistErrors.length > 0;

  return (
    <div className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/60 p-4" onClick={handleBackdrop}>
      <div
        className="flex max-h-[92vh] min-h-[400px] w-full max-w-6xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b border-gray-200 bg-gray-50 px-6 py-4">
          <h3 className="text-xl font-bold text-gray-900">Deploy embedding</h3>
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg p-2 text-gray-500 transition-colors hover:bg-gray-200 hover:text-gray-800"
            aria-label="Close"
          >
            ✕
          </button>
        </div>
        <div className="flex min-h-0 flex-1 flex-col overflow-hidden lg:flex-row">
          <div className="min-w-0 flex-1 overflow-y-auto p-4 sm:p-6">
            {!effectiveScriptId ? (
              <div className="space-y-4">
                <p className="text-gray-600">Choose an agent to embed on your website. You’ll get a code snippet to paste into your page.</p>
                <div>
                  <label className="mb-2 block text-sm font-semibold text-gray-700">Select agent</label>
                  <select
                    value={selectedScriptId}
                    onChange={(event) => setSelectedScriptId(event.target.value)}
                    className="w-full rounded-lg border border-gray-300 px-4 py-3 text-gray-800 focus:outline-none focus:ring-2 focus:ring-[#459EA3]"
                  >
                    <option value="">- Choose an agent -</option>
                    {(scripts || []).map((script) => (
                      <option key={script._id} value={script._id}>
                        {script.scriptName}
                      </option>
                    ))}
                  </select>
                </div>
                {(!scripts || scripts.length === 0) ? (
                  <p className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-2 text-sm text-amber-700">
                    No agents yet. Create one in Agents first, then come back to get the embed code.
                  </p>
                ) : null}
              </div>
            ) : (
              <>
                <div className="mb-4 border-b border-gray-200 pb-4">
                  <p className="text-sm text-gray-500">Embedding agent</p>
                  <p className="font-semibold text-gray-900">{selectedScript?.scriptName || "Agent"}</p>
                </div>

                {settingsError ? (
                  <div className="mb-4 rounded-lg border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
                    {settingsError}
                  </div>
                ) : null}

                <div className="mb-6 space-y-4">
                  <h4 className="text-sm font-semibold text-gray-800">Customize</h4>
                  <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
                    <div className="min-w-0 md:col-span-2 xl:col-span-1">
                      <label className="mb-1.5 block text-xs font-medium text-gray-600">Title</label>
                      <input
                        type="text"
                        value={embedTitle}
                        onChange={(event) => setEmbedTitle(event.target.value)}
                        placeholder="Chat with Maya"
                        className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-[#459EA3]"
                      />
                    </div>
                    <div className="min-w-0">
                      <label className="mb-1.5 block text-xs font-medium text-gray-600">Primary colour</label>
                      <div className="flex min-w-0 items-center gap-2">
                        <input
                          type="color"
                          value={embedPrimaryColor}
                          onChange={(event) => setEmbedPrimaryColor(event.target.value)}
                          className="h-10 w-12 shrink-0 cursor-pointer rounded border border-gray-300 bg-white p-0.5"
                        />
                        <input
                          type="text"
                          value={embedPrimaryColor}
                          onChange={(event) => setEmbedPrimaryColor(event.target.value)}
                          className="min-w-0 flex-1 rounded-lg border border-gray-300 px-3 py-2 font-mono text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-[#459EA3]"
                          placeholder="#459EA3"
                        />
                      </div>
                    </div>
                    <div className="min-w-0">
                      <label className="mb-1.5 block text-xs font-medium text-gray-600">Size</label>
                      <select
                        value={embedSize}
                        onChange={(event) => setEmbedSize(event.target.value)}
                        className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-[#459EA3]"
                      >
                        {EMBED_SIZES.map((size) => (
                          <option key={size.id} value={size.id}>
                            {size.label} ({size.width}x{size.height})
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="min-w-0">
                      <label className="mb-1.5 block text-xs font-medium text-gray-600">Avatar</label>
                      <select
                        value={avatarMode}
                        onChange={(event) => setAvatarMode(event.target.value)}
                        className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-[#459EA3]"
                      >
                        {AVATAR_OPTIONS.map((option) => (
                          <option key={option.id} value={option.id}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>

                <div className="mb-6 rounded-xl border border-gray-200 bg-gray-50 p-4">
                  <div>
                    <h4 className="text-sm font-semibold text-gray-800">Access control</h4>
                    <p className="mt-1 text-sm text-gray-600">
                      Embeds are restricted to exact site origins. Add one allowed origin per line, for example <code className="rounded bg-white px-1 py-0.5">https://example.com</code>.
                    </p>
                  </div>

                  <div className="mt-4 space-y-3">
                    <div>
                      <label htmlFor="embed-allowed-origins" className="mb-1.5 block text-xs font-medium text-gray-600">Allowed site origins</label>
                      <textarea
                        id="embed-allowed-origins"
                        value={allowedOriginsInput}
                        onChange={(event) => setAllowedOriginsInput(event.target.value)}
                        rows={5}
                        placeholder={"https://example.com\nhttps://www.example.com"}
                        className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-[#459EA3]"
                      />
                    </div>
                    {allowlistErrors.length > 0 ? (
                      <div className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">
                        {allowlistErrors.map((error) => (
                          <p key={error}>{error}</p>
                        ))}
                      </div>
                    ) : (
                      <p className="text-xs text-gray-500">
                        Exact-origin matching only. Paths, wildcards, query strings, and hashes are not supported.
                      </p>
                    )}
                  </div>
                </div>

                <EmbedDeployPanel
                  scriptId={effectiveScriptId}
                  avatarMode={avatarMode}
                  width={sizePreset.width}
                  height={sizePreset.height}
                  title={embedTitle}
                  primaryColor={embedPrimaryColor}
                  onCopy={handleCopySnippet}
                  copyDisabled={copyDisabled}
                  copyError={copyError}
                />

                {loadingSettings ? <p className="mt-3 text-sm text-gray-500">Loading current embed settings...</p> : null}
              </>
            )}
          </div>

          {effectiveScriptId ? (
            <div className="flex w-full shrink-0 flex-col border-t border-gray-200 bg-gray-50 p-4 sm:p-5 lg:w-[540px] lg:border-l lg:border-t-0">
              <p className="mb-3 text-xs font-semibold uppercase tracking-wide text-gray-600">Preview</p>
              <div className="flex flex-1 min-h-0 items-start justify-center overflow-auto rounded-xl border border-gray-200 bg-gray-100 pb-4 pt-4">
                <EmbedPreviewPlaceholder
                  width={sizePreset.width}
                  height={Math.min(sizePreset.height, 520)}
                  title={embedTitle || "Chat with Maya"}
                  primaryColor={embedPrimaryColor || "#459EA3"}
                  avatarMode={avatarMode}
                />
              </div>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}

export { EmbedDeployPanel };
